/**
 * 头像库：多套本地插画，编号形如 animal-cat，与服务端保存的自选头像同一口径。
 * 没有自选头像时按稳定身份在全部插画里散列，同一人在列表、资料和消息中保持同一头像。
 */

const files = import.meta.glob('./avatars/*/*.svg', { eager: true, import: 'default', query: '?url' }) as Record<string, string>;

export interface AvatarItem {
  id: string;
  url: string;
}

export interface AvatarSet {
  id: string;
  items: AvatarItem[];
  label: string;
}

const SET_LABELS: [string, string][] = [['animal', '萌宠'], ['fruit', '水果'], ['nature', '自然'], ['dessert', '甜点']];

export const AVATAR_SETS: AvatarSet[] = SET_LABELS.map(([id, label]) => ({
  id,
  label,
  items: Object.entries(files)
    .filter(([path]) => path.startsWith(`./avatars/${id}/`))
    .map(([path, url]) => ({ id: `${id}-${path.slice(path.lastIndexOf('/') + 1, -4)}`, url }))
    .sort((left, right) => left.id.localeCompare(right.id)),
}));

const ALL = AVATAR_SETS.flatMap((set) => set.items);
const BY_ID = new Map(ALL.map((item) => [item.id, item.url]));

/** 自选头像编号是否在头像库里（服务端只校验形状，旧编号或未知编号回落到默认头像）。 */
export function isKnownAvatar(id: null | string | undefined): id is string {
  return typeof id === 'string' && BY_ID.has(id);
}

/** 按稳定身份分配的默认头像编号。 */
export function defaultAvatarId(seed: string): string {
  let hash = 2_166_136_261;
  for (let index = 0; index < seed.length; index += 1) {
    hash = Math.imul(hash ^ seed.charCodeAt(index), 16_777_619);
  }
  return ALL[(hash >>> 0) % ALL.length]!.id;
}

/** 实际显示的头像编号：自选头像优先，否则用默认头像。 */
export function avatarIdFor(seed: string, avatar?: null | string): string {
  return isKnownAvatar(avatar) ? avatar : defaultAvatarId(seed);
}

export function avatarUrl(id: string): string {
  return BY_ID.get(id) ?? ALL[0]!.url;
}
